/**
 * DUTY-1 — the view-side decisions for surfacing the weekly price-duty, as ONE tested authority.
 *
 * The number itself lives in the server authority `priceDutyStatus` (packages/server/src/services/priceDuty.ts,
 * served at GET /api/price-duty). This module holds ONLY the thin "who sees what, and in which tone" rules the
 * two web consumers share — the operator imperative on BuyScreen and the owner accountability tiles on
 * DashboardScreen — so neither screen carries a private copy of the gate (FLOW-2: one authority, not two).
 */

/** The subset of `PriceDutyStatus` the view reads. */
export interface PriceDutyView {
  updatesThisWeek: number;
  staleMaterials: number;
  staleDays: number;
  oldestPriceAgeDays: number | null;
  lastUpdate: { actor: string; ts: string } | null;
}

/**
 * OPERATOR imperative gate: show «به‌روزرسانی با شماست» only to someone who CAN act (`price.write`) AND only
 * when the duty is genuinely pending (priced materials past the stale window). A non-writer or a fresh book
 * sees nothing — freshness is a duty, not noise.
 */
export function shouldPromptPriceDuty<T extends Pick<PriceDutyView, 'staleMaterials'>>(
  canWritePrice: boolean,
  duty: T | null | undefined,
): duty is T {
  return canWritePrice && duty != null && duty.staleMaterials > 0;
}

/** OWNER glance: the pre-existing تازگی قیمت freshness tile goes red once materials are past the stale window. */
export function freshnessTone(
  duty: Pick<PriceDutyView, 'staleMaterials'> | null | undefined,
): 'bad' | 'default' {
  return duty != null && duty.staleMaterials > 0 ? 'bad' : 'default';
}

/** OWNER glance: the accountability tile — good when the office kept prices fresh this week, bad when untouched. */
export function updatesTone(updatesThisWeek: number): 'good' | 'bad' {
  return updatesThisWeek > 0 ? 'good' : 'bad';
}
